import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { OpaqueVideoData } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { ApiService, type Blueprint } from '../../client';
import type { EnhancedToolResult, ToolExecutionContext } from '../types';
import { generateVideoDescriptionFromVideoData } from '../video-state';
import { NO_VIDEO_STATE_MESSAGE } from '../video-state/constants';

import { MediaRegistry } from '../registries/MediaRegistry';

import { buildReferenceStateGetter } from './reference-video-transcript';
import { Tool } from './Tool';

import { getOrFetchBlueprint } from 'js/src/data/blueprints/blueprints-operations';
import { checkpointVideo } from 'js/src/data/video-data';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isLuminaryEmpty } from 'js/src/luminary/luminary-selectors';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { enableOutroSceneGroupOperation } from 'js/src/luminary/luminary-operations';
import { saveRawLuminary } from 'js/src/luminary/utils';
import { isApiError } from 'js/src/types';

export const fetchReferenceVideoSchema = z.object({
    video_id: z
        .number()
        .optional()
        .describe(
            'ID of the video to use as the reference. Use when the user points at a specific video rather than a blueprint. Do not pass together with blueprint_id.',
        ),
    blueprint_id: z
        .number()
        .optional()
        .describe(
            'The blueprint whose reference video to fetch. When the user has selected a blueprint, this is provided in their preferences. Do not pass together with video_id.',
        ),
    apply_global_styles: z
        .boolean()
        .optional()
        .describe("Copy the reference's video styling + outro to your video."),
});
export type FetchReferenceVideoArgs = z.infer<typeof fetchReferenceVideoSchema>;

const REFERENCE_VIDEO_HEADER =
    '## Reference Video State\n' +
    '_This is the reference video — the video to imitate. It is NOT the video you are editing._\n\n';

const REFERENCE_MEDIA_HEADER =
    '## Reference Media\n' +
    `_The reference video's media, indexed like search results. Reuse an id with ${TOOL_NAMES.setMedia} where ` +
    'the very same asset belongs in the new video (a logo, a brand clip, an outro image); for ' +
    'everything else, search for media in the same style as these._\n';

/**
 * The reference's template settings, extracted the way `applyBlueprintTemplateSettingsOperation`
 * does it: load the video into a throwaway engine and save its template back out, so every setting
 * the template owns comes across rather than a hand-picked subset. Null when it has no template.
 */
function loadReferenceTemplate(
    referenceVideoData: OpaqueVideoData,
): ReturnType<Luminary['template']['save']> | null {
    const dataLuminary = new Luminary({
        fetchResources: false,
        disableRenderer: true,
        autoDraw: false,
        autoUpdate: false,
    });
    try {
        dataLuminary.load(referenceVideoData);
        return dataLuminary.template.id ? dataLuminary.template.save() : null;
    } catch (error) {
        ErrorReporter.capture(error, { fingerprint: ['fetch-reference-video-template-load'] });
        return null;
    } finally {
        dataLuminary.destroy();
    }
}

/** Distinguishes the reference's snapshot from the turn's own before/after video snapshots. */
const REFERENCE_SNAPSHOT_LABEL = 'Reference video';

/**
 * The reference's media as the agent sees search results — same registry representations, so the
 * ids are directly usable. Empty when the reference carries no media.
 */
function formatReferenceMedia(mediaIds: string[]): string {
    const mediaRegistry = MediaRegistry.getInstance();
    const representations = mediaIds
        .map((id) => mediaRegistry.getRepresentation(id))
        .filter((representation) => representation !== null);
    if (representations.length === 0) {
        return '';
    }
    return `\n\n${REFERENCE_MEDIA_HEADER}${JSON.stringify(representations)}`;
}

function formatBlueprintSettings(blueprint: Blueprint): string {
    const lines: string[] = [
        `- Default template: ${blueprint.default_template_id}`,
        `- Default aspect ratio: ${blueprint.default_aspect_ratio}`,
        `- Narrative thread: ${blueprint.narrative_thread}`,
        `- Target duration: ${blueprint.default_target_duration_seconds} seconds`,
    ];
    if (blueprint.ai_voice) {
        lines.push(`- AI voice: ${blueprint.ai_voice}`);
    }
    if (blueprint.description) {
        lines.push(`- Description: ${blueprint.description}`);
    }
    if (blueprint.supported_templates.length > 0) {
        lines.push(
            `- Templates with reference examples: ${blueprint.supported_templates.join(', ')}`,
        );
    }
    return lines.join('\n');
}

/**
 * Fetches a reference video — either a blueprint's example video or any video
 * the user can access — and returns its full annotation, so the agent can
 * build a new video that matches the reference's script style and visual
 * choices. The blueprint path resolves the example's video id off the
 * blueprint and then fetches it like any other, additionally returning the
 * blueprint's settings and custom instructions.
 */
export class FetchReferenceVideoTool extends Tool<typeof fetchReferenceVideoSchema> {
    protected override readonly schema = fetchReferenceVideoSchema;
    protected override readonly toolName = TOOL_NAMES.fetchReferenceVideo;
    protected override readonly toolDescription =
        "Fetch a reference video's complete scene-by-scene annotation (script text, scene designs, media, durations) to imitate its style. Pass blueprint_id when the user selected a blueprint (also returns the blueprint's settings and custom instructions); pass video_id to reference one of the user's own videos directly. Call this BEFORE building a video that should match an existing video's style.";

    public override getToolDisplayName(): string {
        return 'Studying reference video';
    }

    public get statusIcon(): string {
        return 'video_library';
    }

    public async execute(
        args: FetchReferenceVideoArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { video_id: videoId, blueprint_id: blueprintId } = args;

        if (videoId && blueprintId) {
            return {
                result: 'Error: Pass either video_id or blueprint_id, not both.',
                isSuccess: false,
            };
        }
        const applyStyles = args.apply_global_styles === true;
        if (videoId) {
            return this.fetchByVideoId(videoId, applyStyles);
        }
        if (blueprintId) {
            return this.fetchByBlueprintId(blueprintId, applyStyles);
        }
        return {
            result: 'Error: Either video_id or blueprint_id is required.',
            isSuccess: false,
        };
    }

    /**
     * Serializes the reference video into its annotation, or null when the
     * video has no scenes (the serializer's "no video yet" message describes
     * the agent's own video, which is exactly what a reference must not be
     * confused with).
     */
    private async describeReference(
        videoData: OpaqueVideoData,
        transcripts?: Record<string, Record<string, unknown>>,
    ): Promise<string | null> {
        // Overlay the reference video's transcript editors onto the session state so its narration
        // renders with per-scene coverage and pauses — identical to get_video_state.
        const getState = buildReferenceStateGetter(this.getState(), transcripts);
        let referenceMediaIds: string[] = [];
        const annotation = await generateVideoDescriptionFromVideoData(videoData, {
            header: REFERENCE_VIDEO_HEADER,
            getState,
            // Index the reference's media before it is serialized, so every media asset in the
            // annotation carries a real title and an id set_media accepts. Built from the assets'
            // own source data: a public blueprint's example lives in another workspace, where
            // fetching its media by uuid would be denied.
            onLoaded: (luminary) => {
                referenceMediaIds =
                    MediaRegistry.getInstance().addUnseenItemsFromSourceData(luminary);
            },
        });
        if (annotation === NO_VIDEO_STATE_MESSAGE) {
            return null;
        }
        return `${annotation}${formatReferenceMedia(referenceMediaIds)}`;
    }

    private async fetchAndDescribe(
        videoId: number,
    ): Promise<{ name: string; annotation: string | null; videoData: OpaqueVideoData }> {
        const videoResponse = await ApiService.videoRetrieve({
            path: { video_id: String(videoId) },
        });
        const video = videoResponse.data;
        const videoData = video.video_data as OpaqueVideoData;

        return {
            name: video.name,
            annotation: await this.describeReference(videoData, video.transcripts),
            videoData,
        };
    }

    /**
     * Recreate the reference's outro scene on the video being edited, when it has one.
     *
     * An outro is branding rather than content — the same logo, URL and colors close every video
     * made from a reference — so it belongs with the global styling rather than being rebuilt from
     * scratch per video. Luminary allows exactly one outro and owns its placement, so this routes
     * through `enableOutroSceneGroup` and then copies the reference outro's text and media onto the
     * scene it creates. Returns a note for the tool result, or an empty string when there is
     * nothing to copy.
     */
    private applyReferenceOutro(
        referenceVideoData: OpaqueVideoData,
        referenceTemplate: OpaqueVideoData,
    ): string {
        const referenceOutro = (
            referenceVideoData as { slides?: { sceneCategory?: string; sceneDesign?: string }[] }
        ).slides?.find((slide) => slide.sceneCategory === VideoData.CATEGORY_OUTRO);
        if (!referenceOutro?.sceneDesign) {
            return '';
        }

        // Luminary allows exactly one outro and owns where it sits, so create it through the same
        // operation the editor uses rather than inserting a scene group by hand.
        const { enableOutroSceneGroup } = makeOperationsCallable(
            { enableOutroSceneGroupOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const targetTemplate = this.luminary.template.save();
        const [referenceTemplateId] = referenceOutro.sceneDesign.split(':');
        // A reference on another template names a design this video doesn't have; fall back to the
        // target template's own default outro so the scene still gets created.
        const sceneDesignId =
            referenceTemplateId === this.luminary.template.id
                ? referenceOutro.sceneDesign
                : undefined;

        try {
            enableOutroSceneGroup(false, undefined, sceneDesignId);
            const outro = this.luminary.getOutroSceneGroup();
            if (!outro) {
                return '';
            }
            // duplicateSceneData remaps the scene's `themeRef_` colour, font and watermark
            // references onto the target template — the reason to use it rather than copying assets
            // across by hand, which would leave references pointing at slots that may not exist.
            outro.scene.load(
                VideoData.duplicateSceneData(
                    referenceOutro as OpaqueVideoData,
                    referenceTemplate,
                    outro.scene.sceneDesign,
                    targetTemplate,
                ),
            );
            return `outro scene (${outro.scene.sceneDesign})`;
        } catch (error) {
            ErrorReporter.capture(error, {
                fingerprint: ['fetch-reference-video-outro'],
                extra: { sceneDesignId },
            });
            return '';
        }
    }

    /**
     * Copy the reference's global styling onto the video being edited: its whole template (colors,
     * fonts, watermark) plus its outro scene.
     *
     * `template.load()` is the same call `applyBlueprintTemplateSettingsOperation` uses to put a
     * blueprint's styling on a video, so a reference video styles a video exactly the way a
     * blueprint does. Per-scene content stays the agent's job; the outro is the exception because
     * it is branding — the same logo and closing line end every video made from the reference.
     */
    private applyGlobalStyles(referenceVideoData: OpaqueVideoData): string {
        // A template bundle, not just a scene, is required — `create_blank_video` produces one
        // with no scenes yet, and that is exactly when the agent should be styling. Guarding on
        // scenes (as an earlier version did) rejects the correct call and silently leaves the
        // video unstyled. Guarding on `template?.id` alone is not enough either: the id defaults
        // to a random uuid before any bundle is ever loaded, so it's truthy even when there is no
        // real template to style — which let this proceed into `applyReferenceOutro`, whose
        // `enableOutroSceneGroup` call needs the bundle's scene designs and throws without them.
        if (!this.luminary.isTemplateBundleSet) {
            return (
                'Error: apply_global_styles needs a video to style — create the video first, then ' +
                'call this again with apply_global_styles to copy the reference styling onto it.'
            );
        }

        const applied: string[] = [];
        const referenceTemplate = loadReferenceTemplate(referenceVideoData);
        if (referenceTemplate) {
            this.luminary.template.load(referenceTemplate);
            applied.push('template styling');
        }

        const outroNote = referenceTemplate
            ? this.applyReferenceOutro(referenceVideoData, referenceTemplate as OpaqueVideoData)
            : '';
        if (outroNote) {
            applied.push(outroNote);
        }

        if (applied.length === 0) {
            return 'Note: the reference carries no global styling to apply.';
        }

        // The styling is already on the luminary; the checkpoint only records it for undo. A
        // scene-less video cannot be serialized at all (saveRawLuminary rejects it), and that is
        // the normal case here — styling right after create_blank_video. Whatever adds the first
        // scene checkpoints this change along with it.
        if (isLuminaryEmpty(this.luminary)) {
            return `Applied the reference's ${applied.join(' and ')} to your video.`;
        }
        // Any other serialization failure (a video mid-edit) must not lose the styling or fail the
        // fetch either, so report it and carry on.
        try {
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup: undefined,
                }),
            );
        } catch (error) {
            ErrorReporter.capture(error, {
                fingerprint: ['fetch-reference-video-style-checkpoint'],
            });
        }
        return `Applied the reference's ${applied.join(' and ')} to your video.`;
    }

    private async fetchByVideoId(
        videoId: number,
        applyStyles: boolean,
    ): Promise<EnhancedToolResult> {
        const { name, annotation, videoData } = await this.fetchAndDescribe(videoId);
        if (annotation === null) {
            return {
                result: `Error: Reference video "${name}" (id ${videoId}) has no scenes to imitate. Ask the user for a different reference video, or build without one.`,
                isSuccess: false,
            };
        }

        const styleNote = applyStyles ? `\n\n${this.applyGlobalStyles(videoData)}` : '';

        return {
            result: `Success. Fetched reference video "${name}" (id ${videoId}).${styleNote}

${annotation}`,
            isSuccess: true,
            videoDataSnapshot: videoData,
            videoDataSnapshotLabel: REFERENCE_SNAPSHOT_LABEL,
        };
    }

    private async fetchByBlueprintId(
        blueprintId: number,
        applyStyles: boolean,
    ): Promise<EnhancedToolResult> {
        const blueprint = await this.dispatch(getOrFetchBlueprint(blueprintId));
        if (!blueprint) {
            return {
                result: `Error: Blueprint ${blueprintId} was not found or is not accessible.`,
                isSuccess: false,
            };
        }

        // The agent imitates the example's style, not its template, so any of the blueprint's
        // examples will do — take the default one, falling back to whichever exists.
        const exampleVideoId =
            blueprint.supported_templates_data[blueprint.default_template_id]?.video_id ??
            Object.values(blueprint.supported_templates_data)[0]?.video_id;
        if (exampleVideoId === undefined) {
            return {
                result: `Error: Blueprint ${blueprintId} ("${blueprint.name}") has no reference video. Ask the user for a different reference`,
                isSuccess: false,
            };
        }

        let referenceAnnotation: string | null;
        let referenceVideoData: OpaqueVideoData;
        try {
            ({ annotation: referenceAnnotation, videoData: referenceVideoData } =
                await this.fetchAndDescribe(exampleVideoId));
        } catch (error) {
            // A public blueprint's example can live in another workspace, which this fetch has no
            // grant for. That combination isn't expected to reach this tool, so report it rather
            // than papering over it — a hit here means the assumption no longer holds.
            if (isApiError(error) && (error.status === 403 || error.status === 404)) {
                ErrorReporter.capture(error, {
                    fingerprint: ['fetch-reference-video-blueprint-example-denied'],
                    extra: {
                        blueprintId,
                        exampleVideoId,
                        isPublicBlueprint: blueprint.is_public,
                    },
                });
                return {
                    result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) could not be read. Ask the user for a different reference.`,
                    isSuccess: false,
                };
            }
            throw error;
        }

        if (referenceAnnotation === null) {
            return {
                result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) has no scenes to imitate. Ask the user for a different reference.`,
                isSuccess: false,
            };
        }

        const customInstructionsSection = blueprint.custom_instructions
            ? `\n\n## Custom Instructions (follow these)\n${blueprint.custom_instructions}`
            : '';
        const styleNote = applyStyles ? `\n\n${this.applyGlobalStyles(referenceVideoData)}` : '';

        return {
            result: `Success. Fetched the reference video for blueprint "${blueprint.name}" (id ${blueprintId}).${styleNote}

## Blueprint Settings
${formatBlueprintSettings(blueprint)}${customInstructionsSection}

${referenceAnnotation}`,
            isSuccess: true,
            videoDataSnapshot: referenceVideoData,
            videoDataSnapshotLabel: REFERENCE_SNAPSHOT_LABEL,
        };
    }
}
